import { TiptapEditor } from "../../component/tiptapEditor"
import CustomSelect from "../../component/customSelect/customSelect"
import CustomInput from "../../component/CustomInput/CustomInput"
import Header from "../../component/header/header"
import "./CreateQuestion.style.scss"
import { useCallback, useEffect, useMemo, useState } from "react"
import { SingleValue } from "react-select"
import {
  editSingleQuestion,
  getQuestion,
  postSingleQuestion,
} from "../../api/question"
import { QuestionPayload, QuestionStatus, SubPart } from "../../api/type"
import toast from "react-hot-toast"
import { FiArrowLeft, FiCheckCircle } from "react-icons/fi"
import { useNavigate, useParams } from "react-router-dom"
import { LoaderComponent } from "../../utils/Helper"
import { sanitizedMarkeupContent } from "../../lib/convertLatexContent"
import { GRADES } from "../../utils/grade"
import { complexities } from "../../utils/complexity"
import { buildSubParts, formatSubPartsLabels } from "../../utils/subParts"
import { SelectOption } from "../../component/customSelect/customSelect"
import { SubjectSelectOption } from "../../types/subjectSelect"
import { listSubject } from "src/api/subject"
import Button from "src/component/button/button"
import { getYearValidationError, normalizeYearInput, parseYearOrNull } from "../../utils/yearValidation"
import LevelSelectors, {
  EMPTY_LEVEL_VALUES,
  LevelFieldErrors,
  LevelFieldNames,
  LevelFieldValues,
} from "../Questions/components/LevelSelectors"
import {
  DEFAULT_LEVEL_LABELS,
  normalizeLabels,
} from "../Subjects/detail/hierarchyUtils"
import {
  hasLevelFieldErrors,
  validateQuestionHierarchyLevels,
} from "../Questions/utils/hierarchyValidation"

type SubjectOption = SubjectSelectOption

type FormErrors = {
  subject: string
  grade: string
  complexity: string
  question: string
  year: string
  levels: LevelFieldErrors
}

type QuestionFormState = {
  subject: SubjectOption | null
  levels: LevelFieldValues
  grade: SelectOption | null
  complexity: SelectOption | null
  source: string
  year: string
  marks: string
  question: string
  answer: string
  solution: string
  markingScheme: string
  subPartsText: string
  questionStatus: QuestionStatus
}

const EMPTY_ERRORS: FormErrors = {
  subject: "",
  grade: "",
  complexity: "",
  question: "",
  year: "",
  levels: {},
}

const INITIAL_FORM: QuestionFormState = {
  subject: null,
  levels: { ...EMPTY_LEVEL_VALUES },
  grade: null,
  complexity: null,
  source: "",
  year: "",
  marks: "",
  question: "",
  answer: "",
  solution: "",
  markingScheme: "",
  subPartsText: "",
  questionStatus: "draft",
}

const sanitizeHtml = (value?: string | null) =>
  value ? sanitizedMarkeupContent(value) : ""

const CreateQuestion = () => {
  const navigate = useNavigate()
  const { id } = useParams()

  const [form, setForm] = useState<QuestionFormState>(INITIAL_FORM)
  const [errors, setErrors] = useState<FormErrors>(EMPTY_ERRORS)
  const [subjects, setSubjects] = useState<SubjectOption[]>([])
  const [levelNames, setLevelNames] = useState<LevelFieldNames>({})
  const [isSaving, setIsSaving] = useState(false)
  const [isInitialized, setIsInitialized] = useState(!id)
  const [preservedSubParts, setPreservedSubParts] = useState<SubPart[]>([])

  const isEditing = Boolean(id)
  const isFormReady = !isEditing || isInitialized
  const statusLabel =
    form.questionStatus === "published" ? "Published" : "Draft"

  const updateField = useCallback(
    <K extends keyof QuestionFormState>(
      key: K,
      value: QuestionFormState[K],
    ) => {
      setForm((prev) => ({ ...prev, [key]: value }))
    },
    [],
  )

  const fetchSubjects = useCallback(async () => {
    try {
      const res = await listSubject(1, "", 100000)
      const apiSubjects = res.data?.data?.subjects ?? []
      setSubjects(
        apiSubjects.map((subject: any) => ({
          value: subject.id,
          label: subject.title || "",
          topics: [],
          chapters: [],
          level_labels: Array.isArray(subject.level_labels)
            ? subject.level_labels
            : undefined,
        })),
      )
    } catch (error) {
      console.error("Failed to fetch subjects:", error)
      setSubjects([])
    }
  }, [])

  useEffect(() => {
    fetchSubjects()
  }, [fetchSubjects])

  const hydrateForm = useCallback(
    (data: any) => {
      const selectedSubject = subjects.find(
        (item) => item.value === data.subject,
      )

      setLevelNames({
        level_1: data.topic ?? null,
        level_2: data.level_2_name ?? null,
        level_3: data.level_3_name ?? null,
        level_4: data.level_4_name ?? null,
      })

      setForm({
        subject: selectedSubject ?? null,
        levels: {
          level_1: data.topic_id ?? "",
          level_2: data.level_2_id ?? "",
          level_3: data.level_3_id ?? "",
          level_4: data.level_4_id ?? "",
        },
        grade: GRADES.find((item) => item.value === data.grade) ?? null,
        complexity:
          complexities.find((item) => item.value === data.complexity) ?? null,
        source: data.source ?? "",
        year: data.year ? String(data.year) : "",
        marks: data.marks ? String(data.marks) : "",
        question: sanitizeHtml(data.question),
        answer: sanitizeHtml(data.answer),
        solution: sanitizeHtml(data.solution),
        markingScheme: sanitizeHtml(data.marking_scheme),
        subPartsText: formatSubPartsLabels(data.sub_parts),
        questionStatus:
          data.question_status === "published"
            ? "published"
            : data.question_status === "archived"
              ? "archived"
              : "draft",
      })
      setPreservedSubParts(data.sub_parts ?? [])
      setIsInitialized(true)
    },
    [subjects],
  )

  useEffect(() => {
    if (!id || isInitialized || subjects.length === 0) return

    getQuestion(id)
      .then((res) => hydrateForm(res?.data?.data))
      .catch((error) => {
        console.error(error)
        toast.error("Failed to load question")
        navigate("/questions")
      })
  }, [hydrateForm, id, isInitialized, navigate, subjects.length])

  const subjectLabels = useMemo(
    () =>
      normalizeLabels(
        form.subject?.level_labels && form.subject.level_labels.length
          ? form.subject.level_labels
          : DEFAULT_LEVEL_LABELS,
      ),
    [form.subject],
  )

  const handleSubjectChange = (value: SingleValue<SubjectOption>) => {
    setForm((prev) => ({
      ...prev,
      subject: value,
      levels: { ...EMPTY_LEVEL_VALUES },
    }))
    setLevelNames({})
    setErrors((prev) => ({ ...prev, subject: "", levels: {} }))
  }

  const buildPayload = (status: QuestionStatus): QuestionPayload => ({
    subject: form.subject?.value ?? "",
    topic_id: form.levels.level_1 || "",
    level_2_id: form.levels.level_2 || null,
    level_3_id: form.levels.level_3 || null,
    level_4_id: form.levels.level_4 || null,
    grade: form.grade?.value ?? "",
    complexity: form.complexity?.value ?? "",
    source: form.source.trim() || null,
    year: parseYearOrNull(form.year),
    marks: form.marks ? Number(form.marks) : null,
    question_status: status,
    question: form.question.replaceAll("&amp;", "&"),
    answer: form.answer || null,
    solution: form.solution || null,
    marking_scheme: form.markingScheme || null,
    sub_parts: buildSubParts(form.subPartsText, preservedSubParts),
  })

  const validateForm = (status: QuestionStatus) => {
    const nextErrors: FormErrors = { ...EMPTY_ERRORS, levels: {} }

    if (!form.subject?.value) {
      nextErrors.subject = "Subject is required"
    } else {
      const levelErrors = validateQuestionHierarchyLevels(
        form.levels,
        subjectLabels
      )
      if (hasLevelFieldErrors(levelErrors)) {
        nextErrors.levels = levelErrors
      }
    }

    if (!form.grade?.value) nextErrors.grade = "Level is required"
    if (!form.complexity?.value) {
      nextErrors.complexity = "Difficulty is required"
    }

    if (status === "published" && !form.question.trim()) {
      nextErrors.question = "Question is required to publish"
    }

    const yearError = getYearValidationError(form.year)
    if (yearError) nextErrors.year = yearError

    setErrors(nextErrors)
    const hasLevelError = Object.values(nextErrors.levels).some(Boolean)
    const hasScalarError = [
      nextErrors.subject,
      nextErrors.grade,
      nextErrors.complexity,
      nextErrors.question,
      nextErrors.year,
    ].some((error) => error !== "")
    return !hasLevelError && !hasScalarError
  }

  const notifySuccess = (status: QuestionStatus) => {
    const message = isEditing
      ? status === "published"
        ? form.questionStatus === "published"
          ? "Question saved successfully."
          : "Question published successfully."
        : "Question draft updated successfully."
      : status === "published"
        ? "Question published successfully."
        : "Question saved as draft."

    toast.success(message, {
      icon: <FiCheckCircle color={"var(--success-600)"} size={58} />,
    })
  }

  const handleSubmit = async (status: QuestionStatus) => {
    if (!validateForm(status)) return

    setIsSaving(true)
    const payload = buildPayload(status)

    try {
      if (isEditing && id) {
        await editSingleQuestion(id, payload)
      } else {
        await postSingleQuestion(payload)
      }

      notifySuccess(status)
      navigate("/questions")
    } catch (error) {
      console.error(error)
      toast.error("Unable to save the question. Please try again.")
    } finally {
      setIsSaving(false)
    }
  }

  const actionLabel = useMemo(() => {
    if (isSaving) return "Saving..."
    return isEditing ? "Update" : "Save"
  }, [isEditing, isSaving])

  const isPublishedEdit = isEditing && form.questionStatus === "published"

  const publishButtonLabel = useMemo(() => {
    if (isSaving) return isPublishedEdit ? "Saving..." : "Publishing..."
    return isPublishedEdit ? "Save" : "Publish"
  }, [isPublishedEdit, isSaving])

  return (
    <div className='content create_question'>
      <Header
        pageTitle={`${isEditing ? "Edit" : "Create"} Question`}
        buttonText='All questions'
        buttonIcon={<FiArrowLeft size={18} />}
        type='secondary'
        onClick={() => navigate("/questions")}
        // para={
        //   <span className='create_question__status-badge'>{statusLabel}</span>
        // }
      />

      {isFormReady ? (
        <div className='create_question_content'>
          <aside className='left'>
            <div className='select subject_select'>
              <label htmlFor='subject'>
                Subject <span>*</span>
              </label>
              <CustomSelect
                placeholder='Select subject'
                id='subject'
                data={subjects}
                onChange={handleSubjectChange}
                value={form.subject}
                error={errors.subject}
              />
            </div>

            {form.subject?.value && (
              <div className='select level_select'>
                <LevelSelectors
                  subjectId={form.subject.value}
                  levelLabels={form.subject.level_labels}
                  values={form.levels}
                  names={levelNames}
                  onChange={(next, nextNames) => {
                    updateField("levels", next)
                    setLevelNames(nextNames)
                    setErrors((prev) => ({ ...prev, levels: {} }))
                  }}
                  errors={errors.levels}
                />
              </div>
            )}

            <div className='field'>
              <label htmlFor='subParts'>Sub-parts</label>
              <textarea
                name='subParts'
                id='subParts'
                className='create_question__textarea'
                placeholder='e.g. a, b, c'
                value={form.subPartsText}
                onChange={(event) =>
                  updateField("subPartsText", event.target.value)
                }
                title='Sub-part labels separated by commas'
              />
            </div>

            <div className='select grade_select'>
              <label htmlFor='grade'>
                Level <span>*</span>
              </label>
              <CustomSelect
                placeholder='Select level'
                id='grade'
                data={GRADES}
                onChange={(value: SingleValue<SelectOption>) => {
                  updateField("grade", value)
                  setErrors((prev) => ({ ...prev, grade: "" }))
                }}
                value={form.grade}
                error={errors.grade}
              />
            </div>

            <div className='select complexity_select'>
              <label htmlFor='complexity'>
                Difficulty <span>*</span>
              </label>
              <CustomSelect
                placeholder='Select difficulty'
                id='complexity'
                data={complexities}
                onChange={(value: SingleValue<SelectOption>) => {
                  updateField("complexity", value)
                  setErrors((prev) => ({ ...prev, complexity: "" }))
                }}
                value={form.complexity}
                error={errors.complexity}
              />
            </div>

            <div className='field'>
              <label htmlFor='source'>Source</label>
              <CustomInput
                name='source'
                placeholder='Past paper, textbook, original'
                value={form.source}
                onChange={(event) => updateField("source", event.target.value)}
              />
            </div>

            <div className='field'>
              <label htmlFor='year'>Year</label>
              <CustomInput
                name='year'
                placeholder='e.g. 2024'
                value={form.year}
                onChange={(event) => {
                  updateField("year", normalizeYearInput(event.target.value))
                  setErrors((prev) => ({ ...prev, year: "" }))
                }}
                error={errors.year}
              />
            </div>

            <div className='field'>
              <label htmlFor='marks'>Marks</label>
              <CustomInput
                name='marks'
                placeholder='Total marks'
                value={form.marks}
                onChange={(event) => updateField("marks", event.target.value)}
              />
            </div>
          </aside>

          <section className='right'>
            <div className='right_content'>
              <div className='textEditor_container'>
                <TiptapEditor
                  value={form.question}
                  id='question_editor'
                  handleOnChange={(value) => {
                    updateField("question", value)
                    setErrors((prev) => ({ ...prev, question: "" }))
                  }}
                  label='Question'
                  height={360}
                  placeholder='Enter the question…'
                />
                {errors.question && (
                  <p className='field-error'>{errors.question}</p>
                )}
              </div>

              <div className='textEditor_container'>
                <TiptapEditor
                  value={form.answer}
                  id='answer_editor'
                  handleOnChange={(value) => updateField("answer", value)}
                  label='Answer'
                  height={220}
                  placeholder='Enter the answer…'
                />
              </div>

              <div className='textEditor_container'>
                <TiptapEditor
                  value={form.solution}
                  id='solution_editor'
                  handleOnChange={(value) => updateField("solution", value)}
                  label='Full solution'
                  height={280}
                  placeholder='Enter the full solution…'
                />
              </div>

              <div className='textEditor_container'>
                <TiptapEditor
                  value={form.markingScheme}
                  id='marking_scheme_editor'
                  handleOnChange={(value) =>
                    updateField("markingScheme", value)
                  }
                  label='Marking scheme'
                  height={220}
                  placeholder='Describe how marks are awarded…'
                />
              </div>
            </div>

            <div className='create_question__actions'>
              <Button
                type='secondary'
                text={`${actionLabel} draft`}
                disabled={isSaving}
                onClick={() => handleSubmit("draft")}
              />
              <Button
                text={publishButtonLabel}
                disabled={isSaving}
                onClick={() => handleSubmit("published")}
              />
            </div>
          </section>
        </div>
      ) : (
        <LoaderComponent
          label={isEditing ? "Loading question…" : "Preparing form…"}
        />
      )}
    </div>
  )
}

export default CreateQuestion
